import React from "react";
import clsx from "clsx";
import { Controller } from "react-hook-form";
import { labels, errorMessages } from "resources/resources";
// @material-ui/core components
import Box from "@material-ui/core/Box";
import FormGroup from "@material-ui/core/FormGroup";
import FormLabel from "@material-ui/core/FormLabel";
import FormHelperText from "@material-ui/core/FormHelperText";
import Grid from "@material-ui/core/Grid";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import { REGISTRATION_CONSTS, REGEX_PATTERNS, DATASET } from "utils/const";
import { useDataset } from "hooks/useDataset";
import FormDropdown from "components/FormFields/FormDropdown";

export default function UserData({ control, getValues, errors, classes, theme, trigger, ...props }) {
  const { data: USER_COLLECTIVE_TYPES } = useDataset({ name: DATASET.USER_COLLECTIVE_TYPE });
  const showOptionalEmailError =
    errors["email"] !== undefined && !(errors["email"]?.type === "required" && !getValues("email"));

  React.useEffect(() => {
    if (errors["email"]?.type === "required" && !getValues("email")) {
      props.clearErrors?.("email");
    }
  }, [errors, getValues, props]);
  
  return (
    <>
      <Grid container>
        <Grid item xs={12} md={6}>
          <FormDropdown
            control={control}
            name="userColectiveTypeId"
            label="Tipo de Entidade"
            placeholder="Selecione o tipo de entidade"
            options={USER_COLLECTIVE_TYPES || []}
            mappingTo="code"
            required
            requiredMessage={props.intl.formatMessage(errorMessages.ErrorMessage_FieldRequired)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel>Nome do responsável</FormLabel>
            <Controller
              name="contactName"
              control={control}
              component={Box}
              defaultValue=""
              rules={{
                required: {
                  value: true,
                  message: props.intl.formatMessage(errorMessages.ErrorMessage_FieldRequired),
                },
              }}
              render={({ field: { onChange, name, value } }) => (
                <OutlinedInput
                  name={name}
                  value={value}
                  onChange={onChange}
                  fullWidth
                  autoComplete="off"
                  type="text"
                  classes={{
                    notchedOutline: clsx({
                      [classes.borderWarning]: errors["contactName"] !== undefined,
                    }),
                  }}
                />
              )}
            />
            {errors["contactName"] !== undefined && (
              <FormHelperText component={Box} color={theme.palette.warning.main + "!important"}>
                {errors["contactName"].message}
              </FormHelperText>
            )}
          </FormGroup>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel>NIF Entidade</FormLabel>
            <Controller
              name="taxIdNumber"
              control={control}
              component={Box}
              defaultValue=""
              rules={{
                required: {
                  value: true,
                  message: props.intl.formatMessage(errorMessages.ErrorMessage_FieldRequired),
                },
                validate: {
                  maxLength: (value) => {
                    return value.length <= 14 || "O NIF deve ter ate 14 caracteres";
                  },
                },
              }}
              render={({ field: { onChange, name, value } }) => (
                <OutlinedInput
                  name={name}
                  value={value}
                  onChange={onChange}
                  fullWidth
                  autoComplete="off"
                  type="text"
                  classes={{
                    notchedOutline: clsx({
                      [classes.borderWarning]: errors["taxIdNumber"] !== undefined,
                    }),
                  }}
                />
              )}
            />
            {errors["taxIdNumber"] !== undefined && (
              <FormHelperText component={Box} color={theme.palette.warning.main + "!important"}>
                {errors["taxIdNumber"].message}
              </FormHelperText>
            )}
          </FormGroup>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel>
              {props.intl.formatMessage(labels.Label_Auth_ColectiveRegistrationSteps_UserData_Field_MobilePhone)}
            </FormLabel>
            <Controller
              name="mobilePhone"
              defaultValue=""
              control={control}
              component={Box}
              rules={{
                validate: {
                  validPhoneNumber: (value) => {
                    if (
                      value &&
                      (parseInt(value) < REGISTRATION_CONSTS.MIN_LENGTH_PHONE_NUMBER ||
                        parseInt(value) > REGISTRATION_CONSTS.MAX_LENGTH_PHONE_NUMBER)
                    )
                      return props.intl.formatMessage(errorMessages.ErrorMessage_InvalidFormat);
                    return true;
                  },
                },
              }}
              render={({ field: { onChange, name, value } }) => (
                <OutlinedInput
                  name={name}
                  value={value}
                  fullWidth
                  autoComplete="off"
                  type="number"
                  onChange={(e) => {
                    e.target.value = Math.max(0, parseInt(e.target.value)).toString().slice(0, 9);
                    onChange(e);
                    trigger("mobilePhoneConfirmation");
                  }}
                  classes={{
                    notchedOutline: clsx({
                      [classes.borderWarning]: errors["mobilePhone"] !== undefined,
                    }),
                  }}
                />
              )}
            />
            {errors["mobilePhone"] !== undefined && (
              <FormHelperText component={Box} color={theme.palette.warning.main + "!important"}>
                {errors["mobilePhone"].message}
              </FormHelperText>
            )}
          </FormGroup>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel>Confirmação telemóvel</FormLabel>
            <Controller
              name="mobilePhoneConfirmation"
              defaultValue=""
              control={control}
              component={Box}
              rules={{
                validate: {
                  validPhoneNumber: (value) => {
                    if (
                      value &&
                      (parseInt(value) < REGISTRATION_CONSTS.MIN_LENGTH_PHONE_NUMBER ||
                        parseInt(value) > REGISTRATION_CONSTS.MAX_LENGTH_PHONE_NUMBER)
                    )
                      return props.intl.formatMessage(errorMessages.ErrorMessage_InvalidFormat);
                    return true;
                  },
                  requiredIfMobilePhone: (value) => {
                    if (getValues("mobilePhone") && !value)
                      return props.intl.formatMessage(errorMessages.ErrorMessage_FieldRequired);
                    return true;
                  },
                  phoneNumberMatch: (value) => {
                    return (
                      !getValues("mobilePhone") ||
                      value === getValues("mobilePhone") ||
                      props.intl.formatMessage(
                        errorMessages.ErrorMessage_MobilePhone_ConfirmationMobilePhone_Not_Match,
                        {
                          phoneLabel: props.intl.formatMessage(
                            labels.Label_Auth_ColectiveRegistrationSteps_UserData_Field_MobilePhone
                          ),
                        }
                      )
                    );
                  },
                },
              }}
              render={({ field: { onChange, name, value } }) => (
                <OutlinedInput
                  name={name}
                  value={value}
                  fullWidth
                  autoComplete="off"
                  type="number"
                  onChange={(e) => {
                    e.target.value = Math.max(0, parseInt(e.target.value)).toString().slice(0, 9);
                    onChange(e);
                    trigger("mobilePhone");
                  }}
                  classes={{
                    notchedOutline: clsx({
                      [classes.borderWarning]: errors["mobilePhoneConfirmation"] !== undefined,
                    }),
                  }}
                />
              )}
            />
            {errors["mobilePhoneConfirmation"] !== undefined && (
              <FormHelperText component={Box} color={theme.palette.warning.main + "!important"}>
                {errors["mobilePhoneConfirmation"].message}
              </FormHelperText>
            )}
          </FormGroup>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel>Telemóvel alternativo (opcional)</FormLabel>
            <Controller
              name="alternativeMobilePhone"
              defaultValue=""
              control={control}
              component={Box}
              rules={{
                validate: {
                  validPhoneNumber: (value) => {
                    if (
                      value &&
                      (parseInt(value) < REGISTRATION_CONSTS.MIN_LENGTH_PHONE_NUMBER ||
                        parseInt(value) > REGISTRATION_CONSTS.MAX_LENGTH_PHONE_NUMBER)
                    )
                      return props.intl.formatMessage(errorMessages.ErrorMessage_InvalidFormat);
                    return true;
                  },
                },
              }}
              render={({ field: { onChange, name, value } }) => (
                <OutlinedInput
                  name={name}
                  value={value}
                  fullWidth
                  autoComplete="off"
                  type="number"
                  onChange={(e) => {
                    e.target.value = Math.max(0, parseInt(e.target.value)).toString().slice(0, 9);
                    onChange(e);
                  }}
                  classes={{
                    notchedOutline: clsx({
                      [classes.borderWarning]: errors["alternativeMobilePhone"] !== undefined,
                    }),
                  }}
                />
              )}
            />
            {errors["alternativeMobilePhone"] !== undefined && (
              <FormHelperText component={Box} color={theme.palette.warning.main + "!important"}>
                {errors["alternativeMobilePhone"].message}
              </FormHelperText>
            )}
          </FormGroup>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel>
              {props.intl.formatMessage(labels.Label_Auth_ColectiveRegistrationSteps_UserData_Field_Email)}
            </FormLabel>
            <Controller
              name="email"
              defaultValue=""
              control={control}
              component={Box}
              rules={{
                validate: {
                  validEmail: (value) => {
                    if (value && !REGEX_PATTERNS.EMAIL.test(value))
                      return props.intl.formatMessage(errorMessages.ErrorMessage_InvalidFormat);
                    return true;
                  },
                },
              }}
              render={({ field: { onChange, name, value } }) => (
                <OutlinedInput
                  name={name}
                  value={value}
                  fullWidth
                  autoComplete="off"
                  type="text"
                  onChange={(e) => {
                    onChange(e);
                    trigger("emailConfirmation");
                  }}
                  classes={{
                    notchedOutline: clsx({
                      [classes.borderWarning]: showOptionalEmailError,
                    }),
                  }}
                />
              )}
            />
            {showOptionalEmailError && (
              <FormHelperText component={Box} color={theme.palette.warning.main + "!important"}>
                {errors["email"].message}
              </FormHelperText>
            )}
          </FormGroup>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel>Confirmação e-mail (opcional)</FormLabel>
            <Controller
              name="emailConfirmation"
              defaultValue=""
              control={control}
              component={Box}
              rules={{
                validate: {
                  validEmail: (value) => {
                    if (value && !REGEX_PATTERNS.EMAIL.test(value))
                      return props.intl.formatMessage(errorMessages.ErrorMessage_InvalidFormat);
                    return true;
                  },
                  EmailMatch: (value) => {
                    return (
                      value === getValues("email") ||
                      props.intl.formatMessage(errorMessages.ErrorMessage_Email_ConfirmationEmail_Not_Match, {
                        emailLabel: props.intl.formatMessage(
                          labels.Label_Auth_ColectiveRegistrationSteps_UserData_Field_Email
                        ),
                      })
                    );
                  },
                },
              }}
              render={({ field: { onChange, name, value } }) => (
                <OutlinedInput
                  name={name}
                  value={value}
                  fullWidth
                  autoComplete="off"
                  type="text"
                  onChange={(e) => {
                    onChange(e);
                    trigger("email");
                  }}
                  classes={{
                    notchedOutline: clsx({
                      [classes.borderWarning]: errors["emailConfirmation"] !== undefined,
                    }),
                  }}
                />
              )}
            />
            {errors["emailConfirmation"] !== undefined && (
              <FormHelperText component={Box} color={theme.palette.warning.main + "!important"}>
                {errors["emailConfirmation"].message}
              </FormHelperText>
            )}
          </FormGroup>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel>Email Entidade</FormLabel>
            <Controller
              name="companyEmail"
              defaultValue=""
              control={control}
              component={Box}
              rules={{
                required: {
                  value: true,
                  message: props.intl.formatMessage(errorMessages.ErrorMessage_FieldRequired),
                },
                validate: {
                  validEmail: (value) => {
                    if (value && !REGEX_PATTERNS.EMAIL.test(value))
                      return props.intl.formatMessage(errorMessages.ErrorMessage_InvalidFormat);
                    return true;
                  },
                },
              }}
              render={({ field: { onChange, name, value } }) => (
                <OutlinedInput
                  name={name}
                  value={value}
                  fullWidth
                  autoComplete="off"
                  type="text"
                  onChange={(e) => {
                    onChange(e);
                    trigger("companyEmailConfirmation");
                  }}
                  classes={{
                    notchedOutline: clsx({
                      [classes.borderWarning]: errors["companyEmail"] !== undefined,
                    }),
                  }}
                />
              )}
            />
            {errors["companyEmail"] !== undefined && (
              <FormHelperText component={Box} color={theme.palette.warning.main + "!important"}>
                {errors["companyEmail"].message}
              </FormHelperText>
            )}
          </FormGroup>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormGroup>
            <FormLabel>Confirmação e-mail da entidade</FormLabel>
            <Controller
              name="companyEmailConfirmation"
              defaultValue=""
              control={control}
              component={Box}
              rules={{
                required: {
                  value: true,
                  message: props.intl.formatMessage(errorMessages.ErrorMessage_FieldRequired),
                },
                validate: {
                  validEmail: (value) => {
                    if (value && !REGEX_PATTERNS.EMAIL.test(value))
                      return props.intl.formatMessage(errorMessages.ErrorMessage_InvalidFormat);
                    return true;
                  },
                  EmailMatch: (value) => {
                    return (
                      value === getValues("companyEmail") ||
                      props.intl.formatMessage(errorMessages.ErrorMessage_Email_ConfirmationEmail_Not_Match, {
                        emailLabel: "Email Entidade",
                      })
                    );
                  },
                },
              }}
              render={({ field: { onChange, name, value } }) => (
                <OutlinedInput
                  name={name}
                  value={value}
                  fullWidth
                  autoComplete="off"
                  type="text"
                  onChange={(e) => {
                    onChange(e);
                    trigger("companyEmail");
                  }}
                  classes={{
                    notchedOutline: clsx({
                      [classes.borderWarning]: errors["companyEmailConfirmation"] !== undefined,
                    }),
                  }}
                />
              )}
            />
            {errors["companyEmailConfirmation"] !== undefined && (
              <FormHelperText component={Box} color={theme.palette.warning.main + "!important"}>
                {errors["companyEmailConfirmation"].message}
              </FormHelperText>
            )}
          </FormGroup>
        </Grid>
      </Grid>
    </>
  );
}
